import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { format, differenceInCalendarDays } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar as CalendarIcon, Check, Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import useAvailability, {
  AVAILABILITY_MODIFIER_CLASSES,
  STATUS_DOT_CLASS,
  STATUS_LABELS,
  STATUS_ORDER,
} from "@/lib/useAvailability";
import { bookingDates, isWeekendOrHoliday } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATE_WEEKDAY = 49;
const RATE_WEEKEND = 59;

const BEREICHE = [
  "Gastronomie",
  "Hotellerie",
  "Restaurant",
  "Bankett",
  "Firmenevent",
  "Catering-Support",
  "Sonstiges",
];

// Time options in 15-minute increments from 06:00 to 23:45
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 6; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#E3DECE] rounded-none focus:outline-none focus:border-[#BA6D52] focus:ring-0 px-0 py-3 text-[#2C2A26] placeholder:text-[#A8A399] text-base font-light transition-colors";

export default function Anfrage() {
  // date range: { from: Date, to: Date }. `from` allein = 1 Tag ausgewählt.
  const [range, setRange] = useState(undefined);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [bereich, setBereich] = useState("");
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressTimer, setAddressTimer] = useState(null);

  // derived: primary date + days count from range
  const date = range?.from;
  const daysCount = useMemo(() => {
    if (!range?.from) return 1;
    if (!range?.to) return 1;
    return Math.max(1, differenceInCalendarDays(range.to, range.from) + 1);
  }, [range]);

  const fetchAddressSuggestions = (val) => {
    if (addressTimer) clearTimeout(addressTimer);
    const q = (val || "").trim();
    if (q.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/places/autocomplete?q=${encodeURIComponent(q)}`);
        setAddressSuggestions(data?.suggestions || []);
      } catch {
        setAddressSuggestions([]);
      }
    }, 350);
    setAddressTimer(t);
  };

  const applyAddressSuggestion = (s) => {
    // Parse "Bahnhofstrasse 12, 8000 Zürich, Schweiz" → street + city
    const parts = (s.text || "").split(",").map((p) => p.trim());
    const street = parts[0] || s.text;
    const city = parts.slice(1, 3).filter((p) => p && !/^(Schweiz|Switzerland|Deutschland|Germany)$/i.test(p)).join(", ");
    setForm((f) => ({ ...f, strasse: street, ort: city || f.ort }));
    setAddressSuggestions([]);
  };
  const [success, setSuccess] = useState(false);

  const { byStatus, statusOf } = useAvailability();

  const [form, setForm] = useState({
    strasse: "",
    ort: "",
    betrieb: "",
    telefon: "",
    email: "",
    nachricht: "",
    von_uhrzeit: "",
    bis_uhrzeit: "",
    time_flexibility: "fest", // "fest" | "ca" | "absprache"
  });

  // ---------- Cost calculation ----------
  // Hours from von/bis
  const hours = useMemo(() => {
    if (!form.von_uhrzeit || !form.bis_uhrzeit) return 0;
    const [vh, vm] = form.von_uhrzeit.split(":").map(Number);
    const [bh, bm] = form.bis_uhrzeit.split(":").map(Number);
    let mins = bh * 60 + bm - (vh * 60 + vm);
    if (mins <= 0) mins += 24 * 60; // overnight
    return Math.max(0, mins / 60);
  }, [form.von_uhrzeit, form.bis_uhrzeit]);

  // Auto-detect Wochenende & Feiertage pro Einsatztag
  const dayList = useMemo(
    () => (date ? bookingDates(format(date, "yyyy-MM-dd"), daysCount) : []),
    [date, daysCount],
  );
  const weekendDayCount = useMemo(() => dayList.filter(isWeekendOrHoliday).length, [dayList]);
  const weekdayDayCount = dayList.length ? dayList.length - weekendDayCount : daysCount;

  // Spontaneous detection: < 24h ahead
  const isSpontaneous = useMemo(() => {
    if (!date) return false;
    const now = new Date();
    const diffH = (date.getTime() - now.getTime()) / 36e5;
    return diffH > 0 && diffH < 24;
  }, [date]);

  const billableHours = hours > 0 ? Math.max(hours, 4) : 0; // 4h Mindestbuchung
  const isLongterm = daysCount >= 3;
  const laborBase = billableHours * (weekdayDayCount * RATE_WEEKDAY + weekendDayCount * RATE_WEEKEND);
  const laborCost = isSpontaneous ? laborBase * 1.1 : laborBase;

  const totalCost = laborCost;
  const showCostBlock = hours > 0;


  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Stammkunden-Lookup: wenn E-Mail bekannt, Felder automatisch vorbefüllen
  const lookupCustomer = async () => {
    const email = (form.email || "").trim().toLowerCase();
    if (email.length < 5 || !email.includes("@")) return;
    try {
      const { data } = await axios.get(`${API}/admin/customers/lookup?email=${encodeURIComponent(email)}`);
      if (data?.found) {
        setForm((f) => ({
          ...f,
          betrieb: f.betrieb || data.betrieb || "",
          telefon: f.telefon || data.telefon || "",
          ort: f.ort || data.ort || "",
        }));
        if (data.bereich && !bereich) setBereich(data.bereich);
        toast.success("Willkommen zurück! Daten ausgefüllt.");
      }
    } catch {
      // silent fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Bitte ein Einsatzdatum wählen.");
    if (!bereich) return toast.error("Bitte einen Bereich wählen.");
    if (!agbAccepted) return toast.error("Bitte die AGB und Datenschutzerklärung akzeptieren.");

    // Einsatzdauer automatisch aus Uhrzeit ableiten
    const computedDauer = hours > 0
      ? `${hours.toFixed(2).replace(".", ",")} h${daysCount > 1 ? ` × ${daysCount} Tage` : ""}`
      : "";

    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        einsatzdatum: format(date, "yyyy-MM-dd"),
        von_uhrzeit: form.time_flexibility === "absprache" ? "" : form.von_uhrzeit,
        bis_uhrzeit: form.time_flexibility === "absprache" ? "" : form.bis_uhrzeit,
        time_flexibility: form.time_flexibility,
        ort: [form.strasse, form.ort].filter(Boolean).join(", "),
        betrieb: form.betrieb,
        einsatzdauer: computedDauer,
        anzahl_tage: daysCount,
        bereich,
        telefon: form.telefon,
        email: form.email,
        nachricht: form.nachricht,
        agb_accepted: true,
        agb_accepted_at: new Date().toISOString(),
        // cost preview
        estimated_hours: billableHours,
        hourly_rate_eur: RATE_WEEKDAY,
        hourly_rate_weekend_eur: RATE_WEEKEND,
        is_holiday: weekendDayCount > 0,
        is_spontaneous: isSpontaneous,
        is_longterm: isLongterm,
        labor_cost_eur: laborCost,
        travel_distance_km: 0,
        travel_cost_eur: 0,
        estimated_total_eur: totalCost,
      });
      setSuccess(true);
      toast.success("Anfrage erfolgreich gesendet.");
      setForm({ strasse: "", ort: "", betrieb: "", telefon: "", email: "", nachricht: "", von_uhrzeit: "", bis_uhrzeit: "", time_flexibility: "fest" });
      setBereich("");
      setAgbAccepted(false);
      setRange(undefined);
    } catch (err) {
      console.error(err);
      toast.error("Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  // Memoisierte Kalender-Helpers → verhindern Re-Mount und Springen beim Monatswechsel
  const isDayDisabled = useCallback(
    (d) => {
      const past = d < new Date(new Date().setHours(0, 0, 0, 0));
      const s = statusOf(d);
      return past || s === "booked" || s === "unavailable";
    },
    [statusOf],
  );
  const calendarModifiers = useMemo(
    () => {
      const rangeFrom = range?.from;
      const rangeTo = range?.to;
      const inRange = (d) => {
        if (!rangeFrom) return false;
        const t = d.getTime();
        const fromT = rangeFrom.getTime();
        const toT = (rangeTo || rangeFrom).getTime();
        return t >= Math.min(fromT, toT) && t <= Math.max(fromT, toT);
      };
      return {
        ...byStatus,
        // Nur grün wenn Tag NICHT im aktuell ausgewählten Range liegt
        defaultAvailable: (d) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return d >= today && !statusOf(d) && !inRange(d);
        },
        // Terracotta-40%-Layer nur für Tage STRIKT zwischen Start und End
        // (Start & End erhalten den kräftigeren day_range_start/day_range_end Style)
        inSelectedRange: (d) => {
          if (!rangeFrom || !rangeTo) return false;
          const t = d.getTime();
          const fromT = rangeFrom.getTime();
          const toT = rangeTo.getTime();
          return t > Math.min(fromT, toT) && t < Math.max(fromT, toT);
        },
      };
    },
    [byStatus, statusOf, range],
  );
  const calendarModifierClasses = useMemo(
    () => ({
      ...AVAILABILITY_MODIFIER_CLASSES,
      defaultAvailable: AVAILABILITY_MODIFIER_CLASSES.available,
      inSelectedRange: "!bg-[#BA6D52]/30 !text-[#2C2A26]",
    }),
    [],
  );
  const rangeLabel = useMemo(() => {
    if (!range?.from) return "Datum auswählen";
    if (!range.to || range.from.getTime() === range.to.getTime()) {
      return format(range.from, "EEEE, dd. MMMM yyyy", { locale: de });
    }
    return `${format(range.from, "dd.MM.", { locale: de })} – ${format(range.to, "dd.MM.yyyy", { locale: de })} · ${daysCount} Tage`;
  }, [range, daysCount]);

  if (success) {
    return (
      <div data-testid="page-anfrage-success" className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <div className="w-16 h-16 mx-auto rounded-full border border-[#BA6D52] flex items-center justify-center">
          <Check className="text-[#BA6D52]" size={28} />
        </div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mt-8">Danke für Ihre Anfrage</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
          Ich melde mich in Kürze persönlich bei Ihnen.
        </h1>
        <p className="text-[#757067] font-light text-lg mt-6">
          Ihre Anfrage wurde erfolgreich übermittelt. Bei Rückfragen erreichen Sie mich
          jederzeit per E-Mail oder Telefon.
        </p>
        <button
          data-testid="anfrage-reset"
          onClick={() => setSuccess(false)}
          className="mt-12 inline-flex items-center gap-3 border border-[#E3DECE] text-[#2C2A26] hover:border-[#BA6D52] hover:text-[#BA6D52] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
        >
          Neue Anfrage stellen
        </button>
      </div>
    );
  }

  return (
    <div data-testid="page-anfrage">
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Anfrage</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter mt-6">
            Ihren Einsatz anfragen.
          </h1>
          <p className="text-[#757067] font-light text-lg mt-8 max-w-2xl">
            Schildern Sie kurz Ihren Bedarf — ich melde mich zeitnah mit einer
            Rückmeldung und einem konkreten Angebot.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10"
            data-testid="anfrage-form"
          >
            {/* Einsatzdatum (Zeitraum) */}
            <div className="md:col-span-1">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Einsatzdatum *
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="anfrage-datepicker"
                    className={`mt-3 ${inputCls} flex items-center justify-between ${
                      !date ? "text-[#A8A399]" : ""
                    }`}
                  >
                    {rangeLabel}
                    <CalendarIcon size={18} className="text-[#BA6D52]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 bg-[#FAFAF7] border-[#E3DECE] max-w-[calc(100vw-16px)]"
                  align="start"
                  collisionPadding={12}
                  sideOffset={8}
                >
                  <Calendar
                    mode="range"
                    selected={range}
                    onSelect={setRange}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    numberOfMonths={1}
                    locale={de}
                    modifiers={calendarModifiers}
                    modifiersClassNames={calendarModifierClasses}
                    disabled={isDayDisabled}
                    excludeDisabled
                    classNames={{
                      day_range_start:
                        "!bg-[#BA6D52] !text-white !font-semibold hover:!bg-[#A05D45] rounded-l-md",
                      day_range_end:
                        "!bg-[#BA6D52] !text-white !font-semibold hover:!bg-[#A05D45] rounded-r-md",
                      day_range_middle:
                        "!bg-[#BA6D52]/40 !text-[#2C2A26] hover:!bg-[#BA6D52]/50",
                    }}
                  />
                  <div className="border-t border-[#E3DECE] px-3 py-3 text-xs">
                    <p className="text-[#2C2A26] mb-2 font-medium text-[11px] leading-snug">
                      💡 Mehrtägiger Einsatz: <b>Start-Tag klicken</b> → <b>End-Tag klicken</b>.
                    </p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-2">
                      {STATUS_ORDER.map((s) => (
                        <div key={s} className="flex items-center gap-1.5">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT_CLASS[s]}`} />
                          <span className="text-[#2C2A26] text-[11px] truncate">{STATUS_LABELS[s]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Bereich */}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Bereich *
              </Label>
              <Select value={bereich} onValueChange={setBereich}>
                <SelectTrigger
                  data-testid="anfrage-select-bereich"
                  className={`mt-3 ${inputCls} h-auto`}
                >
                  <SelectValue placeholder="Bereich wählen" />
                </SelectTrigger>
                <SelectContent className="bg-[#FAFAF7] border-[#E3DECE] rounded-none">
                  {BEREICHE.map((b) => (
                    <SelectItem key={b} value={b} className="rounded-none">
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Straße */}
            <div className="relative">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Straße &amp; Hausnummer *
              </Label>
              <input
                required
                data-testid="anfrage-input-strasse"
                name="strasse"
                value={form.strasse}
                onChange={(e) => { handleChange(e); fetchAddressSuggestions(e.target.value); }}
                onBlur={() => setTimeout(() => setAddressSuggestions([]), 200)}
                autoComplete="off"
                placeholder="z.B. Bahnhofstrasse 12"
                className={`mt-3 ${inputCls}`}
              />
              {addressSuggestions.length > 0 && (
                <ul data-testid="address-suggestions" className="absolute z-20 mt-1 w-full bg-white border border-[#E3DECE] shadow-lg max-h-72 overflow-y-auto">
                  {addressSuggestions.map((s, idx) => (
                    <li
                      key={idx}
                      onMouseDown={(ev) => { ev.preventDefault(); applyAddressSuggestion(s); }}
                      className="px-3 py-2 text-sm hover:bg-[#FAF5F1] cursor-pointer border-b border-[#E3DECE] last:border-b-0"
                    >
                      {s.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Ort */}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">PLZ &amp; Ort *</Label>
              <input
                required
                data-testid="anfrage-input-ort"
                name="ort"
                value={form.ort}
                onChange={handleChange}
                placeholder="z.B. 64720 Michelstadt"
                className={`mt-3 ${inputCls}`}
              />
            </div>

            {/* Betrieb */}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Betrieb *
              </Label>
              <input
                required
                data-testid="anfrage-input-betrieb"
                name="betrieb"
                value={form.betrieb}
                onChange={handleChange}
                placeholder="Name des Betriebs"
                className={`mt-3 ${inputCls}`}
              />
            </div>

            {/* Anzahl Einsatztage (automatisch aus Datums-Zeitraum) */}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]" htmlFor="anzahl-tage">
                Anzahl Einsatztage
              </Label>
              <div
                id="anzahl-tage"
                data-testid="anfrage-anzahl-tage"
                className={`mt-3 ${inputCls} bg-[#F5F1E8] cursor-not-allowed flex items-center justify-between`}
              >
                <span className="font-medium text-[#2C2A26]">{daysCount}</span>
                <span className="text-xs text-[#757067]">
                  {daysCount === 1 ? " Tag" : " Tage"} · automatisch aus Datum-Zeitraum
                </span>
              </div>
              <p className="text-xs text-[#757067] mt-2 font-light">
                Für mehrere Tage: im Kalender oben <b>Start-Tag klicken</b>, dann <b>End-Tag klicken</b>.
              </p>
            </div>

            {/* Zeitangabe: Fest / Ca / Nach Absprache */}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Zeitangabe
              </Label>
              <Select
                value={form.time_flexibility}
                onValueChange={(v) => setForm((f) => ({ ...f, time_flexibility: v }))}
              >
                <SelectTrigger data-testid="anfrage-select-zeitart" className={`mt-3 ${inputCls} h-auto`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#FAFAF7] border-[#E3DECE] rounded-none">
                  <SelectItem value="fest" className="rounded-none">Feste Zeit</SelectItem>
                  <SelectItem value="ca" className="rounded-none">Ungefähr / Circa · flexibel</SelectItem>
                  <SelectItem value="absprache" className="rounded-none">Nach Absprache</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#757067] mt-2 font-light">
                {form.time_flexibility === "fest" && "Feste Start- und Endzeit — üblich bei Bankett & Events."}
                {form.time_flexibility === "ca" && "Grober Rahmen — die tatsächlichen Zeiten werden vor Ort abgestimmt."}
                {form.time_flexibility === "absprache" && "Wir stimmen die Zeiten individuell vor dem Einsatz ab. Ein finaler Kostenvoranschlag folgt nach Rücksprache."}
              </p>
            </div>

            {/* Von Uhrzeit */}
            {form.time_flexibility !== "absprache" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                {form.time_flexibility === "ca" ? "Von (ca.)" : "Von (Uhrzeit)"}
              </Label>
              <Select value={form.von_uhrzeit} onValueChange={(v) => setForm((f) => ({ ...f, von_uhrzeit: v }))}>
                <SelectTrigger data-testid="anfrage-select-von" className={`mt-3 ${inputCls} h-auto`}>
                  <SelectValue placeholder="Startzeit wählen" />
                </SelectTrigger>
                <SelectContent className="bg-[#FAFAF7] border-[#E3DECE] rounded-none max-h-72">
                  {TIME_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} className="rounded-none">
                      {t} Uhr
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Bis Uhrzeit */}
            {form.time_flexibility !== "absprache" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                {form.time_flexibility === "ca" ? "Bis (ca.)" : "Bis (Uhrzeit)"}
              </Label>
              <Select value={form.bis_uhrzeit} onValueChange={(v) => setForm((f) => ({ ...f, bis_uhrzeit: v }))}>
                <SelectTrigger data-testid="anfrage-select-bis" className={`mt-3 ${inputCls} h-auto`}>
                  <SelectValue placeholder="Endzeit wählen" />
                </SelectTrigger>
                <SelectContent className="bg-[#FAFAF7] border-[#E3DECE] rounded-none max-h-72">
                  {TIME_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} className="rounded-none">
                      {t} Uhr
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Telefon */}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Telefon *
              </Label>
              <input
                required
                data-testid="anfrage-input-telefon"
                name="telefon"
                type="tel"
                value={form.telefon}
                onChange={handleChange}
                placeholder="+49 ..."
                className={`mt-3 ${inputCls}`}
              />
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                E-Mail *
              </Label>
              <input
                required
                type="email"
                data-testid="anfrage-input-email"
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={lookupCustomer}
                placeholder="name@cuisine.de"
                className={`mt-3 ${inputCls}`}
              />
            </div>

            {/* Nachricht */}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Nachricht (optional)
              </Label>
              <textarea
                data-testid="anfrage-input-nachricht"
                name="nachricht"
                rows={4}
                value={form.nachricht}
                onChange={handleChange}
                placeholder="Zusätzliche Informationen, Anforderungen, gewünschte Posten ..."
                className={`mt-3 ${inputCls} resize-none`}
              />
            </div>

            {/* Live-Kostenvorschau */}
            <div className="md:col-span-2 bg-[#F2EFE9] border border-[#E3DECE] p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <Calculator size={18} className="text-[#BA6D52]" />
                <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">
                  Live-Kostenvorschau
                </p>
              </div>

              {!showCostBlock && (
                <p className="text-sm text-[#757067] font-light">
                  Geben Sie <b>Von</b> und <b>Bis</b> an, damit die Kostenvorschau berechnet wird.
                </p>
              )}

              {showCostBlock && (
                <div className="space-y-3 text-sm">
                  <Row label={`Dauer (${hours.toFixed(2).replace(".", ",")} h / Tag, Mindestbuchung 4 h)`} value={`${billableHours.toFixed(2).replace(".", ",")} h`} />
                  {weekdayDayCount > 0 && (
                    <Row
                      label={`Stundensatz Werktag${weekdayDayCount > 1 || weekendDayCount > 0 ? ` (${weekdayDayCount} Tag${weekdayDayCount > 1 ? "e" : ""})` : ""}`}
                      value={`${RATE_WEEKDAY.toFixed(2).replace(".", ",")} € / h`}
                    />
                  )}
                  {weekendDayCount > 0 && (
                    <Row
                      label={`Stundensatz Wochenende/Feiertag (${weekendDayCount} Tag${weekendDayCount > 1 ? "e" : ""})`}
                      value={`${RATE_WEEKEND.toFixed(2).replace(".", ",")} € / h`}
                      highlight
                    />
                  )}
                  {daysCount > 1 && (
                    <Row label="Anzahl Einsatztage" value={`× ${daysCount}`} />
                  )}
                  <Row
                    label={daysCount > 1 ? `Lohnkosten gesamt (${daysCount} Tage)` : "Lohnkosten"}
                    value={`${laborBase.toFixed(2).replace(".", ",")} €`}
                  />
                  {isSpontaneous && (
                    <Row label="Spontanbuchung (< 24 h) +10 %" value={`+ ${(laborBase * 0.1).toFixed(2).replace(".", ",")} €`} highlight />
                  )}
                  <div className="mt-6 pt-6 border-t border-[#E3DECE] flex justify-between items-baseline">
                    <span className="text-[#2C2A26] font-medium uppercase text-xs tracking-[0.2em]">
                      Geschätzte Gesamtsumme
                    </span>
                    <span
                      data-testid="anfrage-total-cost"
                      className="font-serif text-3xl sm:text-4xl text-[#2C2A26]"
                    >
                      {totalCost.toFixed(2).replace(".", ",")}&nbsp;€
                    </span>
                  </div>

                  {isLongterm && (
                    <div
                      data-testid="anfrage-langzeit-notice"
                      className="mt-4 border border-[#BA6D52]/40 bg-[#FAF5F1] p-4"
                    >
                      <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-medium">
                        Langzeit-Einsatz (≥ 3 Tage)
                      </p>
                      <p className="text-sm text-[#2C2A26] font-light mt-2 leading-relaxed">
                        Der oben gezeigte Stundensatz ist noch <b>nicht festgesetzt</b> — bei
                        längeren Einsätzen komme ich Ihnen gerne entgegen. Die endgültige
                        Konditionen vereinbaren wir individuell nach Ihrer Anfrage.
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-[#757067] font-light italic mt-2">
                    Richtwert · alle Beträge zzgl. ges. MwSt. · Endpreis nach individueller Absprache.
                  </p>
                </div>
              )}
            </div>

            {/* AGB-Checkbox */}
            <div className="md:col-span-2 space-y-4">
              <div className="p-4 bg-[#FAF5F1] border-l-4 border-[#BA6D52] text-sm text-[#2C2A26] font-light leading-relaxed"
                data-testid="anfrage-storno-hinweis">
                <b className="text-[#BA6D52]">Terminreservierung:</b> Bestätigte Einsatztage werden
                exklusiv für Ihren Betrieb geblockt und nicht mehr anderweitig vergeben. Eine
                Stornierung ist bis <b>22&nbsp;Tage</b> vor Einsatzbeginn kostenfrei, danach gilt
                eine gestaffelte Ausfallpauschale (40&nbsp;% / 60&nbsp;% / 80&nbsp;% / 90&nbsp;%
                je nach Zeitpunkt). Eine einmalige Verschiebung ist bis 21&nbsp;Tage vorher
                kostenfrei — siehe Ziffer&nbsp;8 der{" "}
                <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#BA6D52] underline hover:no-underline">AGB</a>.
              </div>
              <label htmlFor="anfrage-agb" data-testid="anfrage-agb-label" className="flex items-start gap-3 cursor-pointer group">
                <input
                  id="anfrage-agb"
                  type="checkbox"
                  data-testid="anfrage-agb-checkbox"
                  checked={agbAccepted}
                  onChange={(e) => setAgbAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#BA6D52] cursor-pointer"
                />
                <span className="text-sm text-[#2C2A26] font-light leading-relaxed">
                  Ich habe die <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#BA6D52] underline hover:no-underline">AGB</a> und die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#BA6D52] underline hover:no-underline">Datenschutzerklärung</a> gelesen und akzeptiere diese. Meine Daten werden zur Bearbeitung der Anfrage sowie zur Vertragsabwicklung verwendet (Art. 6 Abs. 1 lit. b DSGVO).
                  <span className="text-[#BA6D52]">*</span>
                </span>
              </label>

            </div>

            {/* Submit */}
            <div className="md:col-span-2 flex items-center justify-end pt-4">
              <button
                type="submit"
                disabled={submitting || !agbAccepted}
                data-testid="anfrage-submit"
                className="inline-flex items-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Wird gesendet …
                  </>
                ) : (
                  "Anfrage absenden"
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, highlight = false }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className={`font-light ${highlight ? "text-[#BA6D52]" : "text-[#757067]"}`}>
        {label}
      </span>
      <span className={`${highlight ? "text-[#BA6D52] font-medium" : "text-[#2C2A26]"}`}>
        {value}
      </span>
    </div>
  );
}
